'use client';

import React, { useCallback } from 'react';
import { Spinner, Chip } from '@heroui/react';
import { Typography, UniversalTable } from '../../../../components';
import { ResponsiveTooltip } from '../../../../components/ui';
import { useTranslations } from '../../../../contexts/translation.context';
import { EmptyContentIc, InfoCircle } from '../../../../components/icons';
import { SubscribersTableProps, RevshareSubscriber } from '../types';
import { getTableColumns } from '../constants';
import IdCell from './IdCell';
import CampaignCell from './CampaignCell';
import UserCell from './UserCell';
import IncomeCell from './IncomeCell';
import DateCell from './DateCell';

const SubscribersTable: React.FC<SubscribersTableProps> = ({
  subscribers,
  isLoading,
  totalCount,
  totalIncome,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) => {
  const { t, lang } = useTranslations();

  const renderCell = useCallback((subscriber: RevshareSubscriber, columnKey: string) => {
    switch (columnKey) {
      case 'id':
        return <IdCell subscriber={subscriber} />;
      case 'campaign':
        return <CampaignCell subscriber={subscriber} />;
      case 'user':
        return <UserCell subscriber={subscriber} />;
      case 'income':
        return <IncomeCell subscriber={subscriber} />;
      case 'date':
        return <DateCell subscriber={subscriber} />;
      default:
        return null;
    }
  }, []);
  // Custom columns with income header badge
  const columns = getTableColumns(t).map((col) => {
    if (col.key === 'income') {
      return {
        ...col,
        name: (
          <div className="flex items-center gap-2">
            <span>{col.name}</span>
            <ResponsiveTooltip
              content={t('partner.revshare.subscribers.table.incomeTooltip')}
              placement="top"
              classNames={{ content: 'max-w-xs' }}
            >
              <span className="cursor-pointer">
                <InfoCircle size={16} />
              </span>
            </ResponsiveTooltip>
            <Chip
              size="sm"
              classNames={{
                base: 'bg-[rgba(212,212,216,0.4)] h-6 px-2',
                content: 'text-auth-muted text-xs font-medium p-0',
              }}
            >
              {/* @ts-ignore */}
              ${Number(totalIncome)?.toFixed(2)}
            </Chip>
          </div>
        ),
      };
    }
    return col;
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner
          size="lg"
          classNames={{
            circle1: '!border-b-partner',
            circle2: '!border-b-partner/50',
          }}
        />
      </div>
    );
  }

  return (
    <UniversalTable
      key={lang}
      data={subscribers}
      columns={columns}
      renderCell={renderCell}
      emptyContent={
        <div className="text-center py-12">
          <div className="flex flex-col items-center gap-4">
            <EmptyContentIc />
            <div className="flex flex-col gap-1.5">
              <Typography variant="h6" className="text-xl font-semibold leading-7">
                {t('partner.revshare.subscribers.noSubscribers')}
              </Typography>
              <Typography
                className="text-neutral-500 text-base font-medium leading-6"
                variant="body1"
              >
                {t('partner.revshare.subscribers.noSubscribersDescription')}
              </Typography>
            </div>
          </div>
        </div>
      }
      pagination={{
        count: totalCount,
        page_size: pageSize,
        page: currentPage,
      }}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      currentPage={currentPage}
      currentPageSize={pageSize}
    />
  );
};

export default SubscribersTable;
